import { AxiosError } from "axios"
import { Link, Navigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"

import { Toast } from "../components/Toast"
import { Spinner } from "../components/Spinner"
import DynamicCheckoutCardItem from "../components/DynamicCheckoutCardItem"
import StripeSubmitButton from "../components/StripeSubmitButton"
import HighlightList from "../components/HighlightList"
import WizardPaywallWidgetOption from "../components/WizardPaywallWidgetOption"
import {
  useAccount,
  useAccountId,
  useAccountUrlPrefix,
  useCreateStripeSession,
} from "../hooks"
import { formatPrice } from "../shared/utils"
import { genericErrMsg } from "../api/auth"
import { useGovernmentStore } from "../stores/useGovernmentStore"
import THREAT_PAGE from "../images/paywall-threat-page.svg"
import ACTION_CALENDAR from "../images/paywall-action-calendar.svg"
import RECOMMENDED_FUNDING from "../images/paywall-recommend-funding.svg"
import { AccountTypes } from "@/types/account"

const GovernmentWizardPaywall = () => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()
  const accountUrlPrefix = useAccountUrlPrefix()

  const { management } = useGovernmentStore()

  const { data: account, isLoading: accountIsLoading } = useAccount<
    AccountTypes,
    Error
  >(queryClient, accountId)

  const { mutateAsync: createStripeSession } = useCreateStripeSession(
    queryClient,
    accountId,
    {
      onSuccess: (data: { url: string }) => {
        window.location.href = data.url
      },
      onError: (error) => {
        const axiosError = error as AxiosError
        Toast.error(axiosError?.message || genericErrMsg)
      },
    }
  )

  const handleStripeSubmit = async () => {
    const postData = {
      lookup_key: "basic_monthly",
      addon_keys: [],
      redirect_path: "checkup/government",
    }

    await createStripeSession(postData)
  }

  const isLoadingArr = [accountIsLoading]

  const isLoading = isLoadingArr.some(Boolean)

  if (isLoading) {
    return (
      <div className="h-full flex-auto flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (account?.is_member) {
    return <Navigate replace to={`${accountUrlPrefix}/`} />
  }

  const price = formatPrice(account?.subscription_pricing.basic_monthly)

  return (
    <div className="bg-white pt-[61px] pb-[74px] h-full overflow-auto">
      <div className="container max-w-[1244px] px-4">
        <h1 className="text-2xl md:text-4xl text-charcoal-500 text-center font-display font-medium leading-[124%] tracking-[0.72px]">
          {management === "member"
            ? "We're excited you chose to become a member!"
            : "Check out what more NCX membership has to offer"}
        </h1>

        <p className="text-charcoal-500 text-lg text-center font-body font-normal leading-[140%] tracking-[0.36px] mt-3">
          {management === "member"
            ? "NCX membership gives you more than just NRCS support and practice stacks."
            : "Being a NCX Member gives you more than just NRCS support and practice stacks."}
        </p>
      </div>

      <div className="container max-w-[752px] mt-7 px-4">
        <WizardPaywallWidgetOption
          heading={
            <>
              <span>NCX membership</span>
              <span className="block md:inline text-xl leading-[140%] tracking-[0.4px] mt-1 md:mt-0 md:ml-4">
                {price}/month for full access
              </span>
            </>
          }
          subheading="Become an NCX Member to access tools and insights to help you decide, plan and take action on your land goals."
          subheadingClass="w-[calc(100%-50px)] text-lg font-body leading-[140%] tracking-[0.36px]"
          enabled={false}
          active={true}
          className="!cursor-auto"
        >
          <div className="flex flex-col gap-2 py-[14px] sm:px-[21px]">
            <DynamicCheckoutCardItem
              img={RECOMMENDED_FUNDING}
              alt="Recommended funding"
              heading="Recommended funding programs that fit your needs"
              text="We sift through thousands of grants, leases, and subsidies to recommend the right ones for you and your land."
            />

            <DynamicCheckoutCardItem
              img={ACTION_CALENDAR}
              alt="Action calendar"
              heading="Personalized action plan"
              text="Know exactly what to do, where on your property, and when to protect your investment."
            />

            <DynamicCheckoutCardItem
              img={THREAT_PAGE}
              alt="threat page"
              heading="Threat monitoring and alerts"
              text="Get notified about tree loss and risks like fire and insect outbreaks before they damage your forest."
            />
          </div>

          <div className="flex flex-col items-center mt-2">
            <p className="text-charcoal-500 text-xs leading-[26px] tracking-[0.36px] mb-3">
              No long term contract. Cancel anytime.
            </p>

            <StripeSubmitButton
              role={"admin"}
              text="Get NCX membership"
              trackingClass="govt-app-join-now"
              handleStripeSubmit={() => {
                handleStripeSubmit()
              }}
            />

            <Link
              to={`${accountUrlPrefix}/programs/page/1?category=saved_programs`}
              className="spb-wizard member-widget-dashboard-link inline-block text-lg text-leaf text-center leading-[140%] tracking-[0.36px] mt-3"
            >
              No thanks, bring me to my Saved Programs
            </Link>
          </div>
        </WizardPaywallWidgetOption>

        <h2 className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px] mt-12">
          Top benefits you can expect out of your NCX membership
        </h2>

        <p className="font-body leading-[140%] tracking-[0.32px] mt-6">
          We are here to help you plan and take action on the goals for your
          land, to gain all the benefits for your life needs, land protection
          and future strategy
        </p>

        <HighlightList
          className="mt-6"
          list={[
            {
              heading: "Access to NCX Experts to give you quality information",
              text: "We offer a trusted voice and sounding board who will help you understand your risks and the tradeoffs of different plan actions.",
            },
            {
              heading:
                "We continuously sort through thousands of grants & subsidies to find the right ones for you.",
              text: "This may include state specific tax credits, cost share assistance programs, or timber replanting and carbon programs.",
            },
            {
              heading:
                "Act as your personal support team, all managed from your desktop or phone.",
              text: "We assess your land, plan with you to maximize your land goals, and help you implement that plan, leaving you more time to enjoy it.",
            },
          ]}
        />
      </div>
    </div>
  )
}

export default GovernmentWizardPaywall
